import React, {
  forwardRef,
  useState,
  useEffect,
  useContext,
  useLayoutEffect,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

import DContent from "./DContent";
// import logo from "../assets/om_logo1.png";
import {
  UserIcon,
  IdentificationIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArrowUturnLeftIcon,
  CalendarDaysIcon,
  UserCircleIcon,
  InboxStackIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

import { ConfigContext } from "../util/ConfigContext";
import { isTokenValid } from "../util/JwtUtil";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { tailChase } from "ldrs";
import { ThemeContext } from "../util/ThemeContext";
import LoadingOverlay from "./LoadingOverlay";
import { logoutWithRedirect } from "../util/JwtUtil";

tailChase.register();

const Dashboard = forwardRef((props, ref) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const { jwt, rawJwt } = useContext(ConfigContext);
  const themeData = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboard = location.pathname === "/dashboard";

  const handleLogoClick = () => {
    if (isDashboard) {
      navigate("/schedule");
    } else {
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    if (!ref?.current) return;
    ref.current.openSidebar = () => {
      setSidebarOpen(true);
    };
    ref.current.closeSidebar = () => {
      setSidebarOpen(false);
    };
  }, []);

  // 🔁 Handle redirection *before paint* to avoid flicker/glitch
  useLayoutEffect(() => {
    if (jwt?.role === "SUBSCRIBER") {
      navigate("/schedule");
      return;
    }

    if (!jwt && !isTokenValid(jwt)) {
      logoutWithRedirect(navigate, jwt?.tenant);
    }
  }, [jwt, navigate]);

  useEffect(() => {
    if (jwt && jwt.role !== "SUBSCRIBER") {
      setActiveTab(selectInitialActive());
    }
  }, [jwt]);

  if (!jwt && !rawJwt && !isTokenValid(jwt)) {
    return <LoadingOverlay type={1} />;
  }

  function selectInitialActive() {
    const telecomRoles = ["OWNER", "SUPER_MASTER", "MASTER", "MANAGER"];

    switch (jwt.role) {
      case "ADMIN":
        return "owners";
      case telecomRoles.find((role) => role === jwt.role):
        return "telecom_hosts";
      default:
        return logoutWithRedirect(navigate, jwt?.tenant); // fallback
    }
  }

  const navigation = [
    {
      name: "Owners",
      key: "owners",
      icon: UserGroupIcon,
      role: ["ADMIN"],
      divider: false,
    },
    {
      name: "All Users",
      key: "all_users",
      icon: IdentificationIcon,
      role: ["OWNER"],
      divider: false,
    },
    // {
    //   name: "Managers",
    //   key: "managers",
    //   icon: UserIcon,
    //   role: ["OWNER"],
    //   divider: false,
    // },
    {
      name: "Hosts",
      key: "hosts",
      icon: UserIcon,
      role: ["OWNER"],
      iv: false,
      divider: false,
    },
    {
      name: "Super Masters",
      key: "telecom_super_masters",
      icon: UsersIcon,
      role: ["OWNER", "MANAGER"],
      divider: false,
    },
    {
      name: "Masters",
      key: "telecom_masters",
      icon: UserGroupIcon,
      role: ["OWNER", "MANAGER", "SUPER_MASTER"],
      divider: false,
    },
    {
      name: "Subscribers",
      key: "telecom_hosts",
      icon: UserCircleIcon,
      role: ["OWNER", "MASTER", "MANAGER", "SUPER_MASTER"],
      divider: false,
    },
    {
      divider: true,
      dividerName: "Zoom",
      name: "Meetings",
      key: "cutting_meetings",
      icon: CalendarDaysIcon,
      role: ["OWNER"],
    },
    {
      name: "Accounts",
      key: "cutting_zoom",
      icon: InboxStackIcon,
      role: ["OWNER"],
      divider: false,
    },
    {
      name: "Transactions",
      key: "transactions",
      icon: BanknotesIcon,
      role: ["OWNER", "ADMIN", "MASTER", "SUPER_MASTER"],
      divider: true,
      dividerName: "Transactions",
    },
    {
      name: "Refunds",
      key: "refunds",
      icon: ArrowUturnLeftIcon,
      role: ["OWNER"],
      divider: false,
    },
    // {
    //   name: "Zoom",
    //   key: "zoom",
    //   icon: VideoCameraIcon,
    //   role: ["ADMIN"],
    // },
  ];

  // Only render the main content when jwt is confirmed (not undefined and not null)
  return (
    <div>
      {/* Sidebar (Mobile) */}
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-0 transition-opacity duration-300 ease-linear" />
        <div className="fixed inset-0 flex">
          <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out">
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white z-50"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex grow flex-col gap-y-5 overflow-y-auto dark:bg-gray-900 bg-dashboardMenuBackgroundColor px-6 pb-4  ring-1 ring-white/10 z-10">
              <div
                onClick={handleLogoClick}
                className="relative top-4 flex h-16 shrink-0 items-center justify-center cursor-pointer z-50"
              >
                <img
                  src={themeData.lightlogoUrl}
                  alt="logo"
                  className="dark:hidden h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 xl:h-28 xl:w-28 object-contain"
                />
                <img
                  src={themeData.darklogourl}
                  alt="logo"
                  className="hidden dark:block h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 xl:h-28 xl:w-28 object-contain"
                />
                {/* <img
                  src={themeData.logoUrl || logo}
                  alt="logo"
                  className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 xl:h-28 xl:w-28 object-contain"
                /> */}
              </div>

              {/* <ProfileDropdown /> */}
              <nav className="flex flex-1 flex-col">
                <ul key={"mobile-side-bar"} className="-mx-2 space-y-1">
                  <span className="flex items-center">
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-800 dark:to-gray-300"></span>

                    <span className="shrink-0 px-4 text-gray-800 dark:text-gray-300">
                      Users
                    </span>

                    <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-800 dark:to-gray-300"></span>
                  </span>
                  {navigation
                    .filter((item) => {
                      const roleMatch = item.role.includes(jwt?.role);
                      const ivMatch =
                        item.iv === undefined || item.iv === jwt?.iv;
                      return roleMatch && ivMatch;
                    })
                    .map((item) => (
                      <>
                        {item.divider && (
                          // <hr class="my-12 h-px border-t-0 bg-transparent bg-gradient-to-r from-transparent via-neutral-500 to-transparent opacity-25 dark:via-neutral-400" />
                          <span className="flex items-center">
                            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-800 dark:to-gray-300"></span>

                            <span className="shrink-0 px-4 text-gray-800 dark:text-gray-300">
                              {item.dividerName}
                            </span>

                            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-800 dark:to-gray-300"></span>
                          </span>
                        )}
                        <li key={item.key}>
                          <button
                            onClick={() => {
                              if (activeTab !== item.key)
                                setActiveTab(item.key);
                              setSidebarOpen(false);
                            }}
                            className={`flex items-center gap-x-3 w-full px-4 py-2 rounded-md text-sm font-medium transition-all
                      ${
                        activeTab === item.key
                          ? "bg-dashboardActiveTabBackgroundColor text-dashboardActiveTabTextAndIconColor" // dark:bg-[#00ADB5] dark:text-black
                          : "bg-dashboardInactiveTabBackgroundColor text-dashboardInactiveTabTextAndIconColor hover:bg-dashboardInactiveTabHoverBackgroundColor hover:text-dashboardInactiveTabHoverTextAndIconColor hover:pl-5 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-blue-500 dark:hover:text-white"
                      }`}
                          >
                            <item.icon
                              strokeWidth={2}
                              className={`h-5 w-5 shrink-0
                            ${
                              activeTab === item.key
                                ? "text-dashboardActiveTabTextAndIconColor dark:group-hover:text-black" // dark:text-black
                                : "text-dashboardInactiveTabTextAndIconColor group-hover:text-dashboardInactiveTabHoverTextAndIconColor transition-transform duration-300 group-hover:scale-110" // dark:text-gray-400
                            }`}
                            />
                            {item.name}
                          </button>
                        </li>
                      </>
                    ))}
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Sidebar (Desktop) */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex ${
          sidebarCollapsed ? "lg:w-0" : "lg:w-72"
        } transition-all duration-500 overflow-hidden`}
      >
        <div className="flex grow flex-col gap-y-5 overflow-y-auto dark:bg-gray-900 bg-dashboardMenuBackgroundColor pb-4">
          <div
            onClick={handleLogoClick}
            className="relative top-8 flex h-24 shrink-0 items-center justify-center cursor-pointer z-10 "
          >
            <img
              src={themeData.lightlogoUrl}
              alt="logo"
              className="dark:hidden h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 xl:h-28 xl:w-28 object-contain"
            />
            <img
              src={themeData.darklogourl}
              alt="logo"
              className="hidden dark:block h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 xl:h-28 xl:w-28 object-contain"
            />
            {/* <img
              src={themeData.logoUrl || logo}
              alt="logo"
              className="h-24 w-24 drop-shadow-lg"
              // style={{ filter: "drop-shadow(2px 4px 6px rgba(0,0,0,0.4))" }}
            /> */}
          </div>

          <nav className="flex flex-1 flex-col gap-y-1 px-2 py-4">
            {/* <ProfileDropdown /> */}
            <ul key={"desktop-sidebar"} className="space-y-1">
              <span className="flex items-center">
                <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-800 dark:to-gray-300"></span>

                <span className="shrink-0 px-4 text-gray-800 dark:text-gray-300">
                  Users
                </span>

                <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-800 dark:to-gray-300"></span>
              </span>
              {navigation
                .filter((item) => {
                  const roleMatch = item.role.includes(jwt?.role);
                  const ivMatch = item.iv === undefined || item.iv === jwt?.iv;

                  // if (item.name === "Hosts") {
                  //   console.log("Debug →", {
                  //     roleMatch,
                  //     ivMatch,
                  //     itemIv: item.iv,
                  //     jwtIv: jwt?.iv,
                  //     result: roleMatch && ivMatch,
                  //   });
                  // }

                  return roleMatch && ivMatch;
                })
                .map((item) => (
                  <React.Fragment key={item.key}>
                    {item.divider && (
                      <span className="flex items-center">
                        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gray-800 dark:to-gray-300"></span>
                        <span className="shrink-0 px-4 text-gray-800 dark:text-gray-300">
                          {item.dividerName}
                        </span>
                        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gray-800 dark:to-gray-300"></span>
                      </span>
                    )}

                    <li key={item.key}>
                      <button
                        onClick={() => {
                          if (activeTab !== item.key) setActiveTab(item.key);
                          setSidebarOpen(false);
                        }}
                        className={`flex items-center gap-x-3 w-full px-4 py-2 rounded-md text-sm font-medium transition-all
                        ${
                          activeTab === item.key
                            ? "bg-dashboardActiveTabBackgroundColor text-dashboardActiveTabTextAndIconColor" // dark:bg-[#00ADB5] dark:text-black
                            : "bg-dashboardInactiveTabBackgroundColor text-dashboardInactiveTabTextAndIconColor hover:bg-dashboardInactiveTabHoverBackgroundColor hover:text-dashboardInactiveTabHoverTextAndIconColor hover:pl-5 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-blue-500 dark:hover:text-white"
                        }`}
                      >
                        <item.icon
                          strokeWidth={2}
                          className={`h-5 w-5 shrink-0 ${
                            activeTab === item.key
                              ? "text-dashboardActiveTabTextAndIconColor" // dark:text-black
                              : "text-dashboardInactiveTabTextAndIconColor group-hover:text-dashboardInactiveTabHoverTextAndIconColor transition-transform duration-300 group-hover:scale-110" // dark:text-gray-400
                          }`}
                        />
                        {item.name}
                      </button>
                    </li>
                  </React.Fragment>
                ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`${
          sidebarCollapsed ? "lg:pl-0" : "lg:pl-72"
        } transition-all duration-500`}
      >
        <main className="min-h-screen relative">
          <div className="sm:px-6 lg:px-8">
            <div className="flex justify-start absolute top-4 left-10 z-10">
              <button
                onClick={() => setSidebarCollapsed((prev) => !prev)}
                className="hidden lg:inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 rounded-full shadow-sm transition-all duration-300 
                hover:text-white 
                  hover:bg-gradient-to-r 
                hover:from-[#374151] hover:to-dashboardActiveTabBackgroundColor
                dark:hover:from-[#1F2937] dark:hover:to-dashboardActiveTabBackgroundColor"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-6 h-6 mr-2 transition-transform duration-300 ${
                    sidebarCollapsed ? "block" : "hidden"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-6 h-6 mr-2 transition-transform duration-300 ${
                    !sidebarCollapsed ? "block" : "hidden"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>

                {sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              </button>
            </div>

            <DContent activeTab={activeTab} />
          </div>
        </main>
      </div>
    </div>
  );
});

export default React.memo(Dashboard);
